import { useMemo } from 'react'
import { LineChart, LineSeries } from 'src/components/analytics/charts/LineChart'
import { NoData } from 'src/components/analytics/common/NoData'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { ChartSkeleton } from '../common/Skeleton'

const EMPTY_CHART = {
  series: [],
  totalConversionRatePercentage: 0,
  conversionRatePercentageChange: 0
}

export default function ConversionRateMultipleSeries() {
  const {
    getters: { selectedTimeScale, refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.conversion_rate_timescale,
      dependencies: {
        endpoints: ['/conversions']
      }
    },
    refreshKey
  })

  const conversionRateData: Omit<LineSeries, 'selectedTimeScale'> & {
    totalConversionRatePercentage: number
    conversionRatePercentageChange: number
  } = useMemo(() => {
    const fromServerData = data[0]?.data?.result?.widgetData?.data
    const rawData = data[0]?.data?.result?.widgetData?.data?.[0]?.data
    const rawComparedData = data[0]?.data?.result?.widgetData?.compared_data?.[0]?.data

    const checkoutInitiatedPercentage = fromServerData?.find(
      (item: any) => item.dimension === 'checkout_initiated_percentage'
    ).data
    const loginCompletedPercentage = fromServerData?.find(
      (item: any) => item.dimension === 'login_completed_percentage'
    ).data
    const paymentPageLoadedPercentage = fromServerData?.find(
      (item: any) => item.dimension === 'address_selected_percentage'
    ).data
    const paymentMethodSelectedPercentage = fromServerData?.find(
      (item: any) => item.dimension === 'payment_method_selected_percentage'
    ).data
    const orderCompletedPercentage = fromServerData?.find(
      (item: any) => item.dimension === 'order_completed_percentage'
    ).data

    if (rawData && rawData.every((item: any) => item.checkout_initiated === 0)) {
      return EMPTY_CHART
    }

    if (!rawData || rawData.length === 0) {
      return EMPTY_CHART
    }

    const sortDataByDate = (prevData: any, currData: any) => {
      const prevDate = new Date(prevData.date)
      const currDate = new Date(currData.date)
      return prevDate.getTime() - currDate.getTime()
    }

    const currentData = rawData.sort(sortDataByDate)
    let previousData: any[] = []
    if (rawComparedData) {
      previousData = rawComparedData.sort(sortDataByDate)
    }

    const calculateTotalConversionRatePercentage = (data: any[]) => {
      const totalUsers = data.reduce((acc, curr) => acc + curr.checkout_initiated, 0)
      if (totalUsers === 0) return 0
      return (data.reduce((acc, curr) => acc + curr.order_completed, 0) / totalUsers) * 100
    }

    const totalConversionRatePercentage = calculateTotalConversionRatePercentage(currentData)
    const previousTotalConversionRatePercentage = calculateTotalConversionRatePercentage(previousData)

    const previousConversionRatePercentageChange =
      previousTotalConversionRatePercentage > 0
        ? ((previousTotalConversionRatePercentage - totalConversionRatePercentage) /
            previousTotalConversionRatePercentage) *
          100
        : 0
    // Create series data for each step in the conversion funnel
    const series = [
      {
        label: 'Checkout Initiated',
        color: '#01CCA7', // Indigo
        data: checkoutInitiatedPercentage.map((item: any) => ({
          ...item
        }))
      },
      {
        label: 'Login Completed',
        color: '#8541A4', // Violet
        data: loginCompletedPercentage.map((item: any) => ({
          ...item
        }))
      },
      {
        label: 'Address Submitted',
        color: '#407BA5', // Pink
        data: paymentPageLoadedPercentage.map((item: any) => ({
          ...item
        }))
      },
      {
        label: 'Payment Method Selected',
        color: '#8CB0C9', // Pink
        data: paymentMethodSelectedPercentage.map((item: any) => ({
          ...item
        }))
      },
      {
        label: 'Order Completed',
        color: '#16A34A', // Green
        data: orderCompletedPercentage.map((item: any) => ({
          ...item
        }))
      }
    ]

    return {
      series,
      totalConversionRatePercentage: totalConversionRatePercentage,
      conversionRatePercentageChange: parseFloat(previousConversionRatePercentageChange.toFixed(2))
    }
  }, [data])

  const totalConversionRatePercentage = conversionRateData.totalConversionRatePercentage
  const conversionRatePercentageChange = conversionRateData.conversionRatePercentageChange

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (conversionRateData.series.length > 0) {
      return (
        <>
          {/* Temporarily removed */}
          {/* <div className='flex items-center gap-2 my-2'>
            <div className='text-[18px] font-bold text-carbon mb-1'>{totalConversionRatePercentage.toFixed(2)}%</div>
            <TrendIndicator value={conversionRatePercentageChange} formatter={value => `${Math.abs(value)}%`} />
          </div> */}
          <div className='h-[90%] w-full'>
            <LineChart series={conversionRateData.series} isPercentage={true} selectedTimeScale={selectedTimeScale} />
          </div>
        </>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}
